import type { ReactNode } from "react"
import { forwardRef } from "react"

import type { PathSegment } from "@productlane/lib"
import {
	AGENT_TOKEN,
	EXTERNAL_IDS_SEGMENT,
	hasWildcardSegment,
	isAgentType,
	parseFieldPath,
	parseSecureRef,
	parseTemplateToken,
} from "@productlane/lib"
import { cn } from "@productlane/lib/src/cn"

export type TokenKind =
	| "literal"
	| "brace"
	| "namespace"
	| "sectionId"
	| "key"
	| "separator"
	| "wildcard"
	| "index"
	| "agent"
	| "type"
	| "pattern"
	| "invalid"

export interface HighlightToken {
	text: string
	kind: TokenKind
}

export const TOKEN_TINT: Record<TokenKind, string> = {
	literal: "text-secondary",
	brace: "text-tertiary",
	namespace: "text-green-600 dark:text-green-400",
	sectionId: "text-secondary",
	key: "text-primary",
	separator: "text-tertiary",
	wildcard: "text-amber-600 dark:text-amber-400",
	index: "text-amber-600 dark:text-amber-400",
	agent: "text-purple-600 dark:text-purple-400",
	type: "text-sky-600 dark:text-sky-400",
	pattern: "text-secondary",
	invalid: "text-red-600 underline decoration-wavy dark:text-red-400",
}

const NAMESPACES = ["widget", "http", "contact", "company"] as const

function isNamespaceInProgress(head: string): boolean {
	return NAMESPACES.some((namespace) => namespace.startsWith(head))
}

// The display form the editors substitute for a qualified http ref. The name is
// greedy so a title containing `".` still reads whole.
const DISPLAY_REF = /^(http)(\.)(".+")(\.)([A-Za-z][A-Za-z0-9_-]{0,63})$/

/**
 * Colors every supported ref shape, including the `http."name".key` display
 * form. A head still being typed stays green so the prefix does not flash red
 * per keystroke.
 */
export function tokenizeSecureRef(value: string): HighlightToken[] {
	if (!value) return []

	const display = DISPLAY_REF.exec(value)
	if (display) {
		return [
			{ text: display[1]!, kind: "namespace" },
			{ text: display[2]!, kind: "separator" },
			{ text: display[3]!, kind: "sectionId" },
			{ text: display[4]!, kind: "separator" },
			{ text: display[5]!, kind: "key" },
		]
	}

	if (parseSecureRef(value)) {
		const parts = value.split(".")

		const kinds: TokenKind[] =
			parts.length === 3
				? // The middle segment is a connector id for http, and the literal
					// externalIds for a record ref.
					[
						"namespace",
						parts[1] === EXTERNAL_IDS_SEGMENT ? "separator" : "sectionId",
						"key",
					]
				: ["namespace", "key"]
		return parts.flatMap((part, index) => {
			const kind = kinds[index]!
			return index === 0
				? [{ text: part, kind }]
				: [
						{ text: ".", kind: "separator" as TokenKind },
						{ text: part, kind },
					]
		})
	}

	const dot = value.indexOf(".")
	const head = dot === -1 ? value : value.slice(0, dot)

	if (dot === -1) {
		return [
			{
				text: value,
				kind: isNamespaceInProgress(head) ? "namespace" : "invalid",
			},
		]
	}
	if (!NAMESPACES.some((namespace) => namespace === head)) {
		return [{ text: value, kind: "invalid" }]
	}

	const tokens: HighlightToken[] = [
		{ text: head, kind: "namespace" },
		{ text: ".", kind: "separator" },
	]
	const tail = value.slice(dot + 1)
	// A display name still being typed stays gray rather than flashing red.
	if (head === "http" && tail.startsWith('"')) {
		tokens.push({ text: tail, kind: "sectionId" })
	} else if (tail) {
		tokens.push({ text: tail, kind: "invalid" })
	}
	return tokens
}

const AGENT_PREFIX = `${AGENT_TOKEN}.`

/** Colors `agent`, `agent.<path>` and a trailing `.<type>`; null for another head. */
function tokenizeAgentHead(head: string): HighlightToken[] | null {
	if (head === AGENT_TOKEN) return [{ text: head, kind: "agent" }]
	if (!head.startsWith(AGENT_PREFIX)) return null

	const tokens: HighlightToken[] = [
		{ text: AGENT_TOKEN, kind: "agent" },
		{ text: ".", kind: "separator" },
	]
	// A path still being typed keeps its trailing dot rather than flashing red.
	const rest = head.slice(AGENT_PREFIX.length).replace(/\.$/, "")
	const trailingDot = rest.length < head.length - AGENT_PREFIX.length

	if (rest) {
		const dot = rest.lastIndexOf(".")
		const tail = rest.slice(dot + 1)
		const typed = isAgentType(tail)
		const path = typed ? rest.slice(0, Math.max(dot, 0)) : rest
		const segments = path ? parseFieldPath(path) : []

		if (!segments || hasWildcardSegment(segments)) {
			tokens.push({ text: rest, kind: "invalid" })
		} else {
			if (path) tokens.push(...tokenizeFieldPath(path))
			if (typed) {
				if (path) tokens.push({ text: ".", kind: "separator" })
				tokens.push({ text: tail, kind: "type" })
			}
		}
	}

	if (trailingDot) tokens.push({ text: ".", kind: "separator" })
	return tokens
}

/** Reattaches the padding a trimmed head dropped, so the overlay stays aligned. */
function withPad(tokens: HighlightToken[], pad: string): HighlightToken[] {
	if (!pad) return tokens
	const last = tokens[tokens.length - 1]!
	return [...tokens.slice(0, -1), { ...last, text: last.text + pad }]
}

/**
 * Colors one `{{token}}` body: an agent placeholder with its optional path, type
 * and pattern, or a secure ref. Only template bodies know `agent`.
 */
export function tokenizeTokenBody(body: string): HighlightToken[] {
	const pipe = body.indexOf("|")
	if (pipe !== -1) {
		const raw = body.slice(0, pipe)
		const head = tokenizeAgentHead(raw.trimEnd())
		if (!head) return tokenizeSecureRef(body)
		const token = parseTemplateToken(body)
		return [
			...withPad(head, raw.slice(raw.trimEnd().length)),
			{ text: "|", kind: "separator" },
			{
				text: body.slice(pipe + 1),
				kind: token?.kind === "agent" ? "pattern" : "invalid",
			},
		]
	}
	const head = tokenizeAgentHead(body)
	if (head) return head
	// A head still being typed stays tinted rather than flashing red per keystroke.
	if (body && AGENT_TOKEN.startsWith(body)) {
		return [{ text: body, kind: "agent" }]
	}
	return tokenizeSecureRef(body)
}

function pushTokenBody(tokens: HighlightToken[], body: string): void {
	const trimmed = body.trim()
	if (!trimmed) {
		if (body) tokens.push({ text: body, kind: "literal" })
		return
	}
	const start = body.indexOf(trimmed)
	if (start > 0) tokens.push({ text: body.slice(0, start), kind: "literal" })
	tokens.push(...tokenizeTokenBody(trimmed))
	const tail = body.slice(start + trimmed.length)
	if (tail) tokens.push({ text: tail, kind: "literal" })
}

/** Colors literal text around every `{{reference}}`, including an unclosed one. */
export function tokenizeTemplate(value: string): HighlightToken[] {
	const tokens: HighlightToken[] = []
	let pos = 0

	while (pos < value.length) {
		const open = value.indexOf("{{", pos)
		if (open === -1) break
		if (open > pos) {
			tokens.push({ text: value.slice(pos, open), kind: "literal" })
		}
		tokens.push({ text: "{{", kind: "brace" })

		const close = value.indexOf("}}", open + 2)
		if (close === -1) {
			pushTokenBody(tokens, value.slice(open + 2))
			return tokens
		}
		pushTokenBody(tokens, value.slice(open + 2, close))
		tokens.push({ text: "}}", kind: "brace" })
		pos = close + 2
	}

	if (pos < value.length) {
		tokens.push({ text: value.slice(pos), kind: "literal" })
	}
	return tokens
}

function segmentKind(segment: PathSegment): TokenKind {
	switch (segment.kind) {
		case "key":
			return "key"
		case "index":
			return "index"
		default:
			return "wildcard"
	}
}

function singleSegment(chunk: string): PathSegment | null {
	const segments = parseFieldPath(chunk)
	return segments?.length === 1 ? segments[0]! : null
}

/** Colors `metadata.items[].sku`: keys, dots, `[]` and `*` wildcards, indexes. */
export function tokenizeFieldPath(value: string): HighlightToken[] {
	const tokens: HighlightToken[] = []
	let pos = 0

	while (pos < value.length) {
		const char = value[pos]!

		if (char === ".") {
			tokens.push({ text: ".", kind: "separator" })
			pos += 1
			continue
		}

		if (char === "[") {
			const close = value.indexOf("]", pos)
			if (close === -1) {
				tokens.push({ text: value.slice(pos), kind: "invalid" })
				return tokens
			}
			const chunk = value.slice(pos, close + 1)
			const body = value.slice(pos + 1, close)
			const segment = singleSegment(chunk)
			if (!segment) {
				tokens.push({ text: chunk, kind: "invalid" })
			} else if (segment.kind === "anyIndex") {
				tokens.push({ text: chunk, kind: "wildcard" })
			} else {
				tokens.push({ text: "[", kind: "separator" })
				tokens.push({ text: body, kind: segmentKind(segment) })
				tokens.push({ text: "]", kind: "separator" })
			}
			pos = close + 1
			continue
		}

		let end = pos
		while (end < value.length && value[end] !== "." && value[end] !== "[") {
			end += 1
		}
		const chunk = value.slice(pos, end)
		const segment = singleSegment(chunk)
		tokens.push({
			text: chunk,
			kind: segment ? segmentKind(segment) : "invalid",
		})
		pos = end
	}

	return tokens
}

export const TokenText = forwardRef<
	HTMLSpanElement,
	{ tokens: HighlightToken[]; className?: string; children?: ReactNode }
>(({ tokens, className, children }, ref) => (
	<span ref={ref} className={cn("whitespace-pre", className)}>
		{tokens.map((token, index) => (
			<span key={index} className={TOKEN_TINT[token.kind]}>
				{token.text}
			</span>
		))}
		{children}
	</span>
))

export function PathText({
	path,
	className,
}: {
	path: string
	className?: string
}) {
	return <TokenText tokens={tokenizeFieldPath(path)} className={className} />
}
